import {
  UseMediaAccountLinkStatus,
  JobNumberAcquisitionType,
  RegisterChannelUse,
} from './useMedia';
import type { UseMediaAccount, UseMediaListItem, UseMediaListResponse } from './useMedia';
import { MediaType, JobNumberCustomizePattern, LoginConfirmStatus } from './media';
import type {
  MediaListItem,
  LoginCredentialDefinition,
  LoginConfirmResponse,
  TestModeNotificationResponse,
} from './media';
import { InformationCategory } from './informations';
import type { Information } from './informations';
import {
  MailTemplateType,
  VALID_MAIL_TEMPLATE_TAG_IDS,
  JobNumberExtractRuleMaster,
  BranchNumberExtractRuleMaster,
} from '../consts';
import type { MailTemplateItem, SmsTemplateItem, TemplateCreateResponse } from './template';

/** Error thrown when API response validation fails. */
export class ApiResponseValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiResponseValidationError';
  }
}

/** Throws if condition is falsy, surfacing the error via GlobalErrorDialog. */
function assertShape(condition: boolean): asserts condition {
  if (!condition) {
    throw new ApiResponseValidationError('');
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function assertStringArray(data: unknown): asserts data is string[] {
  assertShape(Array.isArray(data) && (data as unknown[]).every((item) => typeof item === 'string'));
}

/** Returns true when data is a plain object whose every value is a string. */
export function isStringRecord(data: unknown): data is Record<string, string> {
  return isObject(data) && Object.values(data).every((v) => typeof v === 'string');
}

/** Validates that value is a numeric member of a numeric enum (excludes reverse-mapped string keys). */
function isValidNumericEnumValue(enumObj: object, value: unknown): boolean {
  return typeof value === 'number' && Object.values(enumObj).includes(value);
}

/** Validates that value is a member of a string enum. */
function isValidStringEnumValue(enumObj: object, value: unknown): boolean {
  return typeof value === 'string' && Object.values(enumObj).includes(value);
}

/** Asserts that data is an array, throwing via GlobalErrorDialog on failure. */
export function assertArray(data: unknown): asserts data is unknown[] {
  if (!Array.isArray(data)) {
    throw new ApiResponseValidationError('');
  }
}

export function assertMediaListItem(data: unknown): asserts data is MediaListItem {
  assertShape(isObject(data));
  assertShape(typeof data.id === 'number');
  assertShape(typeof data.mediaName === 'string');
  assertShape(isValidNumericEnumValue(JobNumberCustomizePattern, data.jobNumberCustomizePattern));
  assertShape(isObject(data.mediaCrawlerInfo) && typeof data.mediaCrawlerInfo.baseUrl === 'string');
  assertShape(isValidNumericEnumValue(MediaType, data.mediaType));
}

export function assertLoginCredentialDefinition(
  data: unknown,
): asserts data is LoginCredentialDefinition {
  assertShape(isObject(data));
  assertShape(isValidNumericEnumValue(MediaType, data.type));
  // For EMAIL media type, template is optional/undefined
  // For other types, template must be a non-empty string
  if (data.type === MediaType.EMAIL) {
    assertShape(data.template === undefined || typeof data.template === 'string');
  } else {
    assertShape(typeof data.template === 'string' && data.template.trim().length > 0);
  }
}

export function assertLoginConfirmResponse(data: unknown): asserts data is LoginConfirmResponse {
  assertShape(isObject(data));
  assertShape(isValidStringEnumValue(LoginConfirmStatus, data.status));
}

export function assertTestModeNotificationResponse(
  data: unknown,
): asserts data is TestModeNotificationResponse {
  assertShape(isObject(data));
  assertShape(
    Array.isArray(data.notificationMessage) &&
      (data.notificationMessage as unknown[]).every((m) => typeof m === 'string'),
  );
  assertShape(
    Array.isArray(data.testApplicationMailaddress) &&
      (data.testApplicationMailaddress as unknown[]).every(
        (m) => isObject(m) && typeof m.mailAddress === 'string',
      ),
  );
  assertShape(
    Array.isArray(data.testApplicationMailDomain) &&
      (data.testApplicationMailDomain as unknown[]).every(
        (d) => isObject(d) && typeof d.mailDomain === 'string',
      ),
  );
}

export function assertUseMediaAccount(data: unknown): asserts data is UseMediaAccount {
  assertShape(isObject(data));
  assertShape(typeof data.useMediaAccountId === 'number');
  assertShape(typeof data.accountAlias === 'string');
  assertShape(typeof data.mediaId === 'number');
  assertShape(typeof data.mediaName === 'string');
  assertShape(isValidNumericEnumValue(UseMediaAccountLinkStatus, data.linkStatus));
  assertShape(isValidNumericEnumValue(JobNumberAcquisitionType, data.jobNumberAcquisitionType));
  assertShape(isValidNumericEnumValue(RegisterChannelUse, data.registerChannelUse));
  assertShape(data.mediaType === undefined || isValidNumericEnumValue(MediaType, data.mediaType));
  assertShape(typeof data.mailAddress === 'string');
  assertShape(
    data.fixedJobNumber === undefined ||
      data.fixedJobNumber === null ||
      typeof data.fixedJobNumber === 'string',
  );
  assertShape(
    data.csvColumn === undefined || data.csvColumn === null || typeof data.csvColumn === 'string',
  );
  assertShape(
    data.extractRule === undefined ||
      data.extractRule === null ||
      JobNumberExtractRuleMaster.some((rule) => rule.code === data.extractRule),
  );
  assertShape(
    data.extractBranchNumberRule === undefined ||
      data.extractBranchNumberRule === null ||
      // 0 means "branch number not used".
      data.extractBranchNumberRule === 0 ||
      BranchNumberExtractRuleMaster.some((rule) => rule.code === data.extractBranchNumberRule),
  );
  assertShape(
    data.registerChannelAlias === undefined || typeof data.registerChannelAlias === 'string',
  );
  assertShape(data.loginCredentials === undefined || typeof data.loginCredentials === 'string');
}

function assertUseMediaListItem(data: unknown): asserts data is UseMediaListItem {
  assertShape(isObject(data));
  assertShape(typeof data.useMediaAccountId === 'number');
  assertShape(typeof data.accountAlias === 'string');
  assertShape(typeof data.mediaId === 'number');
  assertShape(isValidNumericEnumValue(UseMediaAccountLinkStatus, data.linkStatus));
  assertShape(typeof data.mailAddress === 'string');
}

export function assertUseMediaListResponse(data: unknown): asserts data is UseMediaListResponse {
  assertShape(isObject(data));
  const { data: items, total, page, pageSize } = data;
  assertShape(Array.isArray(items));
  for (const item of items) {
    assertUseMediaListItem(item);
  }
  assertShape(typeof total === 'number');
  assertShape(typeof page === 'number');
  assertShape(typeof pageSize === 'number');
}

export function assertInformation(data: unknown): asserts data is Information {
  assertShape(isObject(data));
  assertShape(typeof data.id === 'number');
  assertShape(isValidNumericEnumValue(InformationCategory, data.category));
  assertShape(typeof data.subject === 'string');
  assertShape(data.reference === null || typeof data.reference === 'string');
  assertShape(typeof data.publishedAt === 'string');
}

function assertMailTemplateItem(data: unknown): asserts data is MailTemplateItem {
  assertShape(isObject(data));
  assertShape(typeof data.id === 'number');
  assertShape(typeof data.name === 'string');
  assertShape(typeof data.subject === 'string');
  assertShape(typeof data.body === 'string');
  assertShape(isValidNumericEnumValue(MailTemplateType, data.mailTemplateType));
  assertShape(data.tags === null || typeof data.tags === 'string');
  if (typeof data.tags === 'string' && data.tags !== '') {
    const parts = data.tags.split(',');
    assertShape(parts.every((p) => /^\d+$/.test(p) && VALID_MAIL_TEMPLATE_TAG_IDS.has(Number(p))));
  }
  assertShape(typeof data.createdAt === 'string');
  assertShape(typeof data.updatedAt === 'string');
}

export function assertMailTemplateList(data: unknown): asserts data is MailTemplateItem[] {
  assertShape(Array.isArray(data));
  for (const item of data) {
    assertMailTemplateItem(item);
  }
}

function assertSmsTemplateItem(data: unknown): asserts data is SmsTemplateItem {
  assertShape(isObject(data));
  assertShape(typeof data.id === 'number');
  assertShape(typeof data.name === 'string');
  assertShape(typeof data.body === 'string');
  assertShape(typeof data.createdAt === 'string');
  assertShape(typeof data.updatedAt === 'string');
}

export function assertSmsTemplateList(data: unknown): asserts data is SmsTemplateItem[] {
  assertShape(Array.isArray(data));
  for (const item of data) {
    assertSmsTemplateItem(item);
  }
}

export function assertTemplateCreateResponse(
  data: unknown,
): asserts data is TemplateCreateResponse {
  assertShape(isObject(data));
  assertShape(typeof data.id === 'number');
}
